import { useCallback, useEffect, useMemo, useState } from 'react';
import { Activity, RefreshCw, ShieldCheck } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useManager, type RiskControlPayload, type RiskControlTable } from '../hooks/useManager';

const modeLabels: Record<string, string> = {
  slow: 'Slow',
  normal: 'Normal',
  fast: 'Fast',
};

function money(value: number) {
  return Number(value || 0).toFixed(2);
}

function pct(value: number) {
  return `${Math.round(Number(value || 0) * 100)}%`;
}

function RiskCard({
  row,
  onModeChange,
  saving,
}: {
  row: RiskControlTable;
  onModeChange: (tableId: string, mode: 'slow' | 'normal' | 'fast') => void;
  saving: boolean;
}) {
  const profit = row.tableAccounting?.profit ?? null;
  const observed = row.tableAccounting?.observedRtp ?? null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-bold text-slate-900">{row.tableName}</h3>
            <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${row.live ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
              {row.live ? 'Live' : 'Offline'}
            </span>
          </div>
          <p className="mt-1 text-xs text-slate-500">{row.tableId}</p>
        </div>
        <select
          value={row.recoveryMode}
          disabled={saving}
          onChange={(event) => onModeChange(row.tableId, event.target.value as 'slow' | 'normal' | 'fast')}
          className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-800 shadow-sm focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-200"
        >
          <option value="slow">Slow recovery</option>
          <option value="normal">Normal recovery</option>
          <option value="fast">Fast recovery</option>
        </select>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-5">
        <div className="rounded-lg bg-purple-50 p-3">
          <div className="text-xs font-semibold text-purple-700">Table RTP</div>
          <div className="mt-1 text-xl font-bold text-purple-950">{money(row.currentRtp)}%</div>
        </div>
        <div className="rounded-lg bg-cyan-50 p-3">
          <div className="text-xs font-semibold text-cyan-700">Protected Paid</div>
          <div className="mt-1 text-xl font-bold text-cyan-950">{money(row.protectedPaid)}</div>
        </div>
        <div className="rounded-lg bg-emerald-50 p-3">
          <div className="text-xs font-semibold text-emerald-700">Protected Kills</div>
          <div className="mt-1 text-xl font-bold text-emerald-950">{row.protectedKills}</div>
        </div>
        <div className="rounded-lg bg-rose-50 p-3">
          <div className="text-xs font-semibold text-rose-700">Comfort Debt</div>
          <div className="mt-1 text-xl font-bold text-rose-950">{money(row.comfortDebt)}</div>
        </div>
        <div className="rounded-lg bg-indigo-50 p-3">
          <div className="text-xs font-semibold text-indigo-700">Recovered</div>
          <div className="mt-1 text-xl font-bold text-indigo-950">{money(row.recoveredPaid)}</div>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-5">
        <div className="rounded-lg bg-teal-50 p-3">
          <div className="text-xs font-semibold text-teal-700">Accrued Recovery</div>
          <div className="mt-1 text-xl font-bold text-teal-950">{money(row.accruedRecovery)}</div>
        </div>
        <div className="rounded-lg bg-orange-50 p-3">
          <div className="text-xs font-semibold text-orange-700">Window</div>
          <div className="mt-1 text-xl font-bold text-orange-950">{row.recoveryWindowHours}h</div>
        </div>
        <div className="rounded-lg bg-pink-50 p-3">
          <div className="text-xs font-semibold text-pink-700">Max Take</div>
          <div className="mt-1 text-xl font-bold text-pink-950">{pct(row.maxTakePct)}</div>
        </div>
        <div className="rounded-lg bg-amber-50 p-3">
          <div className="text-xs font-semibold text-amber-700">Live Profit</div>
          <div className="mt-1 text-xl font-bold text-amber-950">{profit == null ? '-' : money(profit)}</div>
        </div>
        <div className="rounded-lg bg-slate-50 p-3">
          <div className="text-xs font-semibold text-slate-700">Live RTP</div>
          <div className="mt-1 text-xl font-bold text-slate-950">{observed == null ? '-' : `${money(observed)}%`}</div>
        </div>
      </div>

      <div className="mt-4 text-sm text-slate-600">
        Mode: <span className="font-semibold text-slate-900">{modeLabels[row.recoveryMode] ?? row.recoveryMode}</span>
        <span> | Floor: {pct(row.minPayoutPct)}</span>
        {row.lastProtectedPayoutAt ? ` | Last protected payout: ${new Date(row.lastProtectedPayoutAt).toLocaleString()}` : ''}
        {row.lastRecoveryAt ? ` | Last recovery: ${new Date(row.lastRecoveryAt).toLocaleString()}` : ''}
      </div>
    </div>
  );
}

export default function RiskControl() {
  const { fetchRiskControl, setTableRiskRecoveryMode } = useManager();
  const [payload, setPayload] = useState<RiskControlPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingTable, setSavingTable] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setPayload(await fetchRiskControl());
    } catch (err: any) {
      toast.error(err?.message || 'Failed to load risk control');
    } finally {
      setLoading(false);
    }
  }, [fetchRiskControl]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const protectedLabel = useMemo(() => {
    if (!payload) return '';
    const tiers = payload.protectedTiers.join(', ');
    const keys = payload.protectedClientKeys.join(', ');
    return [tiers, keys].filter(Boolean).join(' | ');
  }, [payload]);

  const updateMode = async (tableId: string, mode: 'slow' | 'normal' | 'fast') => {
    setSavingTable(tableId);
    try {
      const updated = await setTableRiskRecoveryMode(tableId, mode);
      setPayload((current) => current ? {
        ...current,
        tables: current.tables.map((row) => row.tableId === tableId ? updated : row),
      } : current);
      toast.success('Recovery mode updated');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update recovery mode');
    } finally {
      setSavingTable(null);
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="flex items-center gap-2 text-purple-700">
            <ShieldCheck size={22} />
            <span className="text-sm font-bold uppercase tracking-wide">Table Reserve</span>
          </div>
          <h2 className="mt-1 text-2xl font-bold text-slate-950">Risk Control</h2>
          <p className="mt-1 max-w-3xl text-sm text-slate-600">
            Protected fish payouts are tracked here without changing fish spawn, animation, HP, movement, or collision behavior.
          </p>
        </div>
        <button
          onClick={refresh}
          disabled={loading}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-purple-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-purple-800 disabled:opacity-60"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      <div className="rounded-xl border border-cyan-200 bg-cyan-50 p-4">
        <div className="flex items-center gap-2 font-semibold text-cyan-900">
          <Activity size={18} />
          Protected payout class
        </div>
        <div className="mt-1 text-sm text-cyan-800">{protectedLabel || 'No protected fish configured'}</div>
      </div>

      {loading ? (
        <div className="p-12 text-center text-slate-500">Loading risk control...</div>
      ) : payload?.tables.length ? (
        <div className="space-y-4">
          {payload.tables.map((row) => (
            <RiskCard
              key={row.tableId}
              row={row}
              saving={savingTable === row.tableId}
              onModeChange={updateMode}
            />
          ))}
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 p-12 text-center text-slate-500">
          No tables found.
        </div>
      )}
    </div>
  );
}
